import React from 'react';
import i18n from '../../../../i18n';
import { METRICS } from '../../data/metrics';
import type { ConnectedSourceId } from '../../types';
import { Icon } from './Icon';

/** One Performance drill-down tab; some pair two related metrics. */
export type DrillTabDef = {
  key: string;
  secondaryKey?: string;
  /**
   * Both metrics of the pair are full drills: the tab carries a switch that
   * makes the other one the drill's primary (every tile follows it) while
   * the pair stays in the header and the trend.
   */
  switchable?: boolean;
  label: string;
  icon: string;
};

const POS_TAB_KEYS = new Set(['avgCheck', 'revpash', 'revenue']);
const ALENOPAY_TAB_KEYS = new Set(['sellitems']);

export const DRILL_TABS: DrillTabDef[] = [
  { key: 'covers', get label() { return i18n.t('performance:metrics.covers'); }, icon: 'utensils' },
  {
    key: 'reservations',
    secondaryKey: 'rejected',
    get label() { return i18n.t('performance:metrics.reservations'); },
    icon: 'calendarCheck',
  },
  {
    key: 'noshow',
    secondaryKey: 'cancel',
    switchable: true,
    get label() { return i18n.t('performance:metrics.noshowCancel'); },
    icon: 'flag',
  },
  { key: 'turns', get label() { return i18n.t('performance:metrics.turnsShort'); }, icon: 'refresh' },
  { key: 'brainAutoPct', get label() { return i18n.t('performance:metrics.brainAutoPct'); }, icon: 'table' },
  { key: 'occupancy', get label() { return i18n.t('performance:metrics.occupancy'); }, icon: 'users' },
  { key: 'seatTime', get label() { return i18n.t('performance:metrics.seatTimeShort'); }, icon: 'clock' },
  { key: 'sellitems', get label() { return i18n.t('performance:metrics.sellitemsShort'); }, icon: 'euro' },
  { key: 'avgCheck', get label() { return i18n.t('performance:metrics.avgCheck'); }, icon: 'receipt' },
  { key: 'revpash', get label() { return i18n.t('performance:metrics.revpash'); }, icon: 'banknote' },
  { key: 'revenue', get label() { return i18n.t('performance:metrics.revenue'); }, icon: 'banknote' },
];

/** Primary metric keys in tab order (legacy helper). */
export const DRILLABLE_METRICS = DRILL_TABS.map(t => t.key);

export const REVIEWS_TAB_KEY = 'reviews';

export function visibleDrillTabs(
  connected?: ConnectedSourceId[],
): DrillTabDef[] {
  const src = new Set(connected ?? []);
  return DRILL_TABS.filter(tab => {
    if (POS_TAB_KEYS.has(tab.key) && !src.has('pos')) return false;
    if (ALENOPAY_TAB_KEYS.has(tab.key) && !src.has('alenopay')) return false;
    return true;
  });
}

export function drillTabForKey(active: string): DrillTabDef | undefined {
  return DRILL_TABS.find(
    t => t.key === active || t.secondaryKey === active,
  );
}

export function drillTabIsActive(tab: DrillTabDef, active: string): boolean {
  return tab.key === active || tab.secondaryKey === active;
}

/**
 * The drill's primary and secondary metric for an active key. On a
 * switchable tab the secondary may lead (`cancel` on the no-show tab); on
 * any other tab the tab's own key leads.
 */
export function drillPairFor(
  active: string,
): { tab: DrillTabDef; primary: string; secondary: string | null } | null {
  const tab = drillTabForKey(active);
  if (!tab) return null;
  const primary =
    tab.switchable && tab.secondaryKey === active ? active : tab.key;
  const secondary =
    primary === tab.key ? (tab.secondaryKey ?? null) : tab.key;
  return { tab, primary, secondary };
}

/** The tab definition with `primary` leading, for a switchable pair. */
export function drillTabLedBy(tab: DrillTabDef, primary: string): DrillTabDef {
  if (primary === tab.key || !tab.switchable || !tab.secondaryKey) return tab;
  return { ...tab, key: tab.secondaryKey, secondaryKey: tab.key };
}

export type DrillLensOption = {
  key: string;
  /** The period figure, formatted. */
  value: React.ReactNode;
  /** The change against the compare period, already rendered. */
  trend?: React.ReactNode;
};

/**
 * Which metric of a paired tab leads the drill (No-show | Cancellations).
 * It heads the drill body, where the tiles it governs start: both figures
 * with their change, the chosen one marked, and a line saying that every
 * tile below follows it.
 */
export function DrillPairLens({
  active,
  options,
  onSelect,
}: {
  active: string;
  options: [DrillLensOption, DrillLensOption];
  onSelect: (key: string) => void;
}) {
  return (
    <div className="drill-lens">
      <span className="drill-lens-cap">{i18n.t('performance:drillUi.lensShowing')}</span>
      <div
        className="drill-lens-opts"
        role="radiogroup"
        aria-label={i18n.t('performance:drillUi.pairSwitchAria')}
      >
        {options.map(o => {
          const on = o.key === active;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={on}
              className={`drill-lens-opt${on ? ' is-on' : ''}`}
              onClick={() => {
                if (!on) onSelect(o.key);
              }}
            >
              <span className="drill-lens-dot" aria-hidden />
              <span className="drill-lens-lab">{METRICS[o.key]?.label ?? o.key}</span>
              <span className="drill-lens-val">{o.value}</span>
              {o.trend}
            </button>
          );
        })}
      </div>
      <span className="drill-lens-hint">{i18n.t('performance:drillUi.lensHint')}</span>
    </div>
  );
}

// Top-level navigation shared by every Performance drill-down. `active` is a
// metric key (e.g. "guests") or REVIEWS_TAB_KEY. The Guest rating tab only
// shows when review data is available, so it never dangles as a dead end.
export function DrillMetricTabs({
  active,
  reviewsAvailable,
  connected,
  onSelect,
}: {
  active: string;
  reviewsAvailable?: boolean;
  connected?: ConnectedSourceId[];
  onSelect: (tab: DrillTabDef) => void;
}) {
  const tabs = visibleDrillTabs(connected);
  return (
    <div className="drill-metric-switcher">
      {tabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          className={`drill-metric-tab${drillTabIsActive(tab, active) ? ' active' : ''}`}
          onClick={() => onSelect(tab)}
        >
          <Icon name={tab.icon} size={13} />
          {tab.label}
        </button>
      ))}
      {reviewsAvailable && (
        <button
          type="button"
          className={`drill-metric-tab${active === REVIEWS_TAB_KEY ? ' active' : ''}`}
          onClick={() =>
            onSelect({
              key: REVIEWS_TAB_KEY,
              label: i18n.t('performance:metrics.guestRating'),
              icon: 'star',
            })
          }
        >
          <Icon name="star" size={13} />
          {i18n.t('performance:metrics.guestRating')}
        </button>
      )}
    </div>
  );
}
